import {
	JIRA_PROJECT_STATUS_ARCHIVED,
	TEAM_MANAGED_PROJECT,
	convertToProjectType,
} from '@atlassian/jira-common-constants/src/project-types';
import { ff } from '@atlassian/jira-feature-flagging';
import { performPostRequest } from '@atlassian/jira-fetch';
import FetchError from '@atlassian/jira-fetch/src/utils';
import { createAri } from '@atlassian/jira-platform-ari';
import type { ResourceStoreContext, RouterDataContext } from '@atlassian/jira-router';
import { getProjectKeyId } from '@atlassian/jira-router-resources-utils';
import type { ProjectContext } from '../types';
import { isCurrentUserProjectAdmin } from './get-permission-data';
import {
	PROJECT_BY_KEY_QUERY,
	PROJECT_BY_ID_QUERY,
	PROJECT_BY_ID_QUERY_OLD,
	PROJECT_BY_KEY_QUERY_OLD,
} from './gql';
import type { ProjectResponse, ProjectData, InternalProjectContext } from './types';

export const AGG_NO_PROJECT_DATA = 512;
const fetchProjectByKey = (projectKey: string, cloudId: string): Promise<ProjectResponse> =>
	performPostRequest('/gateway/api/graphql', {
		method: 'POST',
		headers: {
			'content-type': 'application/json',
			'X-ExperimentalApi': 'JiraProject, projectStyle',
		},
		body: JSON.stringify({
			query: ff('fetch-project-avatar-url-in-project-context-resource_7e1me')
				? PROJECT_BY_KEY_QUERY
				: PROJECT_BY_KEY_QUERY_OLD,
			variables: {
				projectKey,
				cloudId,
			},
		}),
	});
const fetchProjectById = (projectId: string, cloudId: string): Promise<ProjectResponse> => {
	const projectAri = createAri({
		resourceOwner: 'jira',
		resourceType: 'project',
		resourceId: projectId,
		cloudId,
	});
	return performPostRequest('/gateway/api/graphql', {
		method: 'POST',
		headers: {
			'content-type': 'application/json',
			'X-ExperimentalApi': 'projectStyle',
		},
		body: JSON.stringify({
			query: ff('fetch-project-avatar-url-in-project-context-resource_7e1me')
				? PROJECT_BY_ID_QUERY
				: PROJECT_BY_ID_QUERY_OLD,
			variables: {
				projectAri,
			},
		}),
	});
};
const mapProjectDataToContext = (project: ProjectData): InternalProjectContext => {
	const { projectId, key, uuid, name, avatar, projectType, projectStyle, status, created } =
		project;
	return {
		projectKey: key,
		projectId: +projectId,
		projectUuid: uuid,
		projectName: name,
		avatarUrl: ff('fetch-project-avatar-url-in-project-context-resource_7e1me')
			? avatar?.medium ?? ''
			: '',
		simplified: projectStyle === TEAM_MANAGED_PROJECT,
		projectType: convertToProjectType(projectType),
		isProjectArchived: status === JIRA_PROJECT_STATUS_ARCHIVED,
		created,
	};
};
const getProjectContextData = async (
	projectKey: string,
	projectId: string,
	cloudId: string,
): Promise<InternalProjectContext> => {
	const { data, errors } = projectKey
		? await fetchProjectByKey(projectKey, cloudId)
		: await fetchProjectById(projectId, cloudId);
	if (errors != null && errors.length > 0) {
		const statusCode = errors[0].extensions?.statusCode;
		throw new FetchError(
			statusCode,
			`Fetch project context via AGG failed. Errors: ${errors.map((err) => err.message).join(', ')}`,
		);
	}
	const projectData = data?.jira?.jiraProject || data?.jira?.jiraProjectByKey;
	if (!projectData) {
		throw new FetchError(
			AGG_NO_PROJECT_DATA,
			'Fetch project context via AGG succeeded but no data received',
		);
	}
	return mapProjectDataToContext(projectData);
};
const checkNonEmptyString = (input: string): boolean =>
	input !== null && input !== undefined && input !== '';
/**
 * Determines if the current user is an admin of the specified project by extracting project identifiers from a route, verifying these identifiers, and concurrently fetching project context and user permissions. It handles errors gracefully by throwing exceptions if data retrieval fails.
 */
// eslint-disable-next-line jira/import/no-anonymous-default-export
export default async (
	matchedRoute: RouterDataContext,
	{ tenantContext: { cloudId } }: ResourceStoreContext,
): Promise<ProjectContext | null> => {
	let { projectKey, projectId } = getProjectKeyId(matchedRoute.match, matchedRoute.query);
	const projectArg = projectKey || projectId;
	if (projectArg == null) {
		return null;
	}
	if (checkNonEmptyString(projectKey) && !Number.isNaN(Number(projectKey))) {
		// In the cases when the value of projectKey is numerical and apprears to be the value of projectId,
		// We take the projectKey as projectId if there isn't existing value for projectId
		projectId = checkNonEmptyString(projectId) ? projectId : projectKey;
		projectKey = '';
	}
	const [projectResult, permissionResult] = await Promise.allSettled([
		getProjectContextData(projectKey, projectId, cloudId),
		isCurrentUserProjectAdmin(projectArg),
	]);
	if (permissionResult.status === 'rejected') {
		throw permissionResult.reason;
	}
	if (projectResult.status === 'rejected') {
		throw projectResult.reason;
	}
	return {
		...projectResult.value,
		isProjectAdmin: permissionResult.value,
	};
};
